import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import {
  descargarExportTmk,
  dispararExportTmk,
  getTmkExportLatest,
  tmkExportJobId,
} from '@lib/services/tmk-export.service';

/** Si el job no termina en este tiempo, cortamos el poll y mostramos error. */
const DEADLINE_MS = 8 * 60 * 1000;

/**
 * Modal de descarga de la lista de contactos del TMK. Al abrir dispara el job,
 * pollea su estado y, apenas termina, baja el archivo solo. La descarga NUNCA
 * falla por timeout: el archivo se genera en background y se baja ya materializado.
 *
 * Solo descargamos el job que ESTE modal disparó (match por id): el poll de
 * "último job" puede devolver uno viejo (terminado y ya borrado) antes de que el
 * dispara cree el nuevo, y bajar ese daba 404. Además hay un deadline de poll
 * para no quedar colgado si el job nunca llega a estado terminal (worker caído).
 */
export function TmkExportModal({ onClose }: { onClose: () => void }) {
  const [descargado, setDescargado] = useState(false);
  const [descargaError, setDescargaError] = useState(false);
  const [vencio, setVencio] = useState(false);
  const [intento, setIntento] = useState(0);
  const disparado = useRef(false);
  const descargadoRef = useRef(false);

  const dispara = useMutation({ mutationFn: dispararExportTmk });

  useEffect(() => {
    if (disparado.current) return;
    disparado.current = true;
    dispara.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deadline: se re-arma en cada intento (mount + "Generar de nuevo").
  useEffect(() => {
    setVencio(false);
    const t = setTimeout(() => setVencio(true), DEADLINE_MS);
    return () => clearTimeout(t);
  }, [intento]);

  const ourId = dispara.data ? tmkExportJobId(dispara.data) : null;

  const q = useQuery({
    queryKey: ['tmk-export-latest', ourId, intento],
    queryFn: getTmkExportLatest,
    refetchInterval: (query) => {
      if (vencio) return false;
      const d = query.state.data;
      const esNuestro = !!ourId && !!d && tmkExportJobId(d) === ourId;
      if (esNuestro && d.estado !== 'corriendo') return false;
      return 2000;
    },
  });
  const job = ourId && q.data && tmkExportJobId(q.data) === ourId ? q.data : null;

  useEffect(() => {
    if (job?.estado === 'terminado' && !descargadoRef.current) {
      descargadoRef.current = true;
      descargarExportTmk(tmkExportJobId(job), job.filename)
        .then(() => setDescargado(true))
        .catch(() => setDescargaError(true));
    }
  }, [job]);

  const errorDuro = dispara.isError || job?.estado === 'error' || descargaError;
  const hayError = errorDuro || (vencio && !descargado);
  const descargando = job?.estado === 'terminado' && !descargado;
  const pct =
    job && job.total > 0 ? Math.min(100, Math.round((job.procesados / job.total) * 100)) : null;
  const mensajeError = errorDuro
    ? 'No pudimos generar la lista. Probá de nuevo.'
    : 'La generación está tardando más de lo esperado. Probá de nuevo.';

  function regenerar() {
    descargadoRef.current = false;
    setDescargado(false);
    setDescargaError(false);
    // reset() limpia dispara.data → ourId queda null hasta que resuelva el nuevo
    // disparo, así el poll no matchea (ni re-descarga) el job viejo ya borrado.
    dispara.reset();
    setIntento((i) => i + 1);
    dispara.mutate();
  }

  return (
    <Overlay onClose={onClose}>
      <div
        className="card"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(460px, 100%)', display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <header className="rowBetween" style={{ alignItems: 'baseline' }}>
          <h3 className="heading-5" style={{ margin: 0 }}>
            Descargar lista de contactos
          </h3>
          <button type="button" className="btn btn-ghost btn--sm" onClick={onClose}>
            Cerrar
          </button>
        </header>

        <TmkExportModalEstado
          descargado={descargado}
          hayError={hayError}
          descargando={descargando}
          mensajeError={mensajeError}
          pct={pct}
          procesados={job?.procesados}
          total={job?.total}
          onClose={onClose}
          regenerar={regenerar}
        />
      </div>
    </Overlay>
  );
}

/**
 * Cuerpo condicional del modal (descargado / error / en progreso). Extraído de
 * `TmkExportModal` para mantener su complejidad cognitiva ≤ 15 (S3776).
 */
function TmkExportModalEstado({
  descargado,
  hayError,
  descargando,
  mensajeError,
  pct,
  procesados,
  total,
  onClose,
  regenerar,
}: {
  descargado: boolean;
  hayError: boolean;
  descargando: boolean;
  mensajeError: string;
  pct: number | null;
  procesados?: number;
  total?: number;
  onClose: () => void;
  regenerar: () => void;
}) {
  if (descargado) {
    return (
      <>
        <p className="status status-success" style={{ display: 'block', margin: 0 }}>
          ✓ Tu archivo se descargó. Por seguridad se elimina del servidor; si lo necesitás de
          nuevo, generá otro.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cerrar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={regenerar}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Download size={15} /> Generar de nuevo
          </button>
        </div>
      </>
    );
  }
  if (hayError) {
    return (
      <>
        <p className="status status-error" style={{ display: 'block', margin: 0 }}>
          {mensajeError}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-primary" onClick={regenerar}>
            Reintentar
          </button>
        </div>
      </>
    );
  }
  return (
    <>
      <p className="text-element-mid" style={{ margin: 0, lineHeight: 1.55 }}>
        {descargando ? (
          'Descargando tu archivo…'
        ) : (
          <>
            Estamos generando tu lista. <strong>Esto puede tardar unos minutos</strong> según la
            cantidad de contactos.
          </>
        )}
      </p>
      <BarraProgreso pct={descargando ? 100 : pct} procesados={procesados} total={total} />
    </>
  );
}

function BarraProgreso({
  pct,
  procesados,
  total,
}: {
  pct: number | null;
  procesados?: number;
  total?: number;
}) {
  return (
    <div>
      <div
        style={{
          height: 8,
          background: 'var(--color-bg-subtle, #eef1f5)',
          borderRadius: 999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct ?? 0}%`,
            height: '100%',
            background: 'var(--color-primary-darker)',
            transition: 'width .3s ease',
          }}
        />
      </div>
      <p className="text-element-mid" style={{ fontSize: 12, marginTop: 6 }}>
        {pct !== null ? `${procesados ?? 0} / ${total ?? 0} (${pct}%)` : 'Preparando…'}
      </p>
    </div>
  );
}

function Overlay({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 100,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>
  );
}
